import { T } from '../../../themes/tokens';

/**
 * Estilos compartidos de las páginas del admin (/admin/home/*).
 *
 * POR QUÉ EXISTE: el tema de MUI de la app (src/theme.js) es CLARO (texto y
 * primary negros, paper #F9F9F9), pero el admin pinta sus páginas sobre
 * fondo OSCURO (AdminHomeLayout: #0b1120). Todo componente de MUI al que no
 * se le da color explícito sale con el tema claro: letras negras sobre
 * fondo oscuro, botones deshabilitados sin contorno visible, papeles
 * blancos. Usar estos presets en vez de dejar los colores por defecto.
 *
 * Los colores salen de los tokens del tema activo (src/themes/*.css): con el
 * tema oscuro son los mismos de AdminContentWrapper y del menú lateral.
 */

export const ADMIN_COLORS = {
  page: T.bgPage, // fondo de la página (main)
  panel: T.bgPanel, // paneles/secciones (Section)
  surface: T.bgSurface, // superficies internas (tarjetas, tablas, editores)
  title: T.textTitle,
  text: T.text,
  muted: T.muted75,
  border: T.border,
  borderStrong: T.border30,
  accent: T.accent, // acento (links, selección, botones outlined)
  accentStrong: T.primary, // botón principal (contained)
  accentStrongHover: T.primaryHover,
  selectedBg: T.accentTint,
  hoverBg: T.accentTint12,
  danger: T.danger,
  disabledText: T.muted50
};

const C = ADMIN_COLORS;

/** Botón secundario (variant="outlined"), visible también deshabilitado. */
export const outlinedButtonSx = {
  textTransform: 'none',
  color: C.accent,
  borderColor: T.accent50,
  '&:hover': { borderColor: C.accent, backgroundColor: C.hoverBg },
  '&.Mui-disabled': { color: C.disabledText, borderColor: C.borderStrong }
};

/** Botón principal (variant="contained"). */
export const containedButtonSx = {
  textTransform: 'none',
  backgroundColor: C.accentStrong,
  color: T.onPrimary,
  '&:hover': { backgroundColor: C.accentStrongHover },
  '&.Mui-disabled': { backgroundColor: T.primary25, color: C.disabledText }
};

/** Botón destructivo (variant="outlined"). */
export const dangerButtonSx = {
  textTransform: 'none',
  color: C.danger,
  borderColor: T.danger45,
  '&:hover': { borderColor: C.danger, backgroundColor: T.danger08 },
  '&.Mui-disabled': { color: C.disabledText, borderColor: C.borderStrong }
};

/** TextField (incluido `select`) sobre fondo oscuro: sx del TextField. */
export const darkFieldSx = {
  '& .MuiInputLabel-root': { color: C.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: C.accent },
  '& .MuiInputLabel-root.Mui-disabled': { color: C.disabledText },
  '& .MuiOutlinedInput-root': {
    color: C.text,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: C.borderStrong },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.border55 },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.accent },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: C.border }
  },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: C.disabledText },
  '& .MuiSelect-icon': { color: C.muted },
  '& .MuiFormHelperText-root': { color: C.muted }
};

/** Panel/sección (mismo estilo que Section de AdminContentWrapper). */
export const panelSx = {
  backgroundColor: C.panel,
  borderRadius: 2,
  border: `1px solid ${C.border}`,
  boxShadow: `0 12px 32px ${T.shadow}`
};

/** Paper de resultados/tablas (el Paper por defecto sale blanco). */
export const darkPaperSx = {
  backgroundColor: C.surface,
  backgroundImage: 'none',
  color: C.text,
  border: `1px solid ${C.border}`
};

/** Celdas de Table sobre fondo oscuro (encabezado y cuerpo). */
export const darkTableSx = {
  '& .MuiTableCell-root': { color: C.text, borderColor: C.border },
  '& .MuiTableCell-head': { backgroundColor: C.panel, color: C.muted, fontWeight: 600 },
  '& .MuiTableRow-root:hover .MuiTableCell-body': { backgroundColor: T.border06 }
};
